import React, { useState } from 'react';
import { Cog6ToothIcon, BuildingOfficeIcon, ShieldCheckIcon, Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import CommonPageHeader from '../components/common/CommonPageHeader';
import OrganizationList from '../components/settings/OrganizationList';
import MFAPolicySettings from '../components/settings/MFAPolicySettings';
import { useAuth } from '../contexts/AuthContext';
import { Organization } from '../types/organization';
import { useLanguage } from '../contexts/LanguageContext';

const Settings = () => {
  const { user } = useAuth();
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<'organizations' | 'mfa-policy'>('organizations');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useLanguage();

  const handleOrganizationUpdate = (updatedOrg: Organization) => {
    setSelectedOrg({
      ...updatedOrg,
      ownerId: selectedOrg?.ownerId || user?.id || ''
    });
  };

  const tabs = [
    {
      id: 'organizations' as const,
      name: t('common.settings.title'),
      icon: BuildingOfficeIcon,
      description: 'Szervezet beállítások',
      color: 'blue',
      bgColor: 'bg-blue-100 dark:bg-blue-900/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
      activeColor: 'bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border-l-2 border-blue-500',
      roles: ['admin', 'super_admin', 'user']
    },
    {
      id: 'mfa-policy' as const,
      name: 'MFA Szabályzat',
      icon: ShieldCheckIcon,
      description: 'Rendszer szintű MFA kötelezettség beállítása',
      color: 'red',
      bgColor: 'bg-red-100 dark:bg-red-900/20',
      iconColor: 'text-red-600 dark:text-red-400',
      activeColor: 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300 border-l-2 border-red-500',
      roles: ['super_admin']
    }
  ];

  const availableTabs = tabs.filter(tab => tab.roles.includes(user?.role || ''));

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as typeof activeTab);
    setIsMobileMenuOpen(false);
  };

  return (
    <div className="flex flex-col h-screen">
      <CommonPageHeader
        title={t('common.settings.title')}
        subtitle="Alkalmazás és szervezet beállítások"
        icon={Cog6ToothIcon}
        actionButton={
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="md:hidden p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
          >
            {isMobileMenuOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        }
      />

      {/* Mobile Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Main Content - Sidebar + Content */}
      <div className="flex-1 flex overflow-hidden pt-4 md:pt-0">
        {/* Sidebar - Navigation */}
        <div className={`
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 transition-transform duration-300 ease-in-out
          fixed md:relative z-50 md:z-auto
          w-64 h-full md:h-auto flex-shrink-0
          bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto
        `}>
          <div className="p-3">
            {/* Mobile close button */}
            <div className="md:hidden flex justify-between items-center mb-4 p-2">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {t('common.settings.title')}
              </h3>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            {/* Compact header */}
            <div className="mb-4 p-3 bg-gradient-to-r from-indigo-500 via-purple-500 to-indigo-600 rounded-lg text-white shadow-md">
              <div className="flex items-center space-x-2">
                <div className="p-1.5 bg-white/20 rounded-md backdrop-blur-sm">
                  <Cog6ToothIcon className="h-4 w-4" />
                </div>
                <div>
                  <h3 className="font-semibold text-sm">{t('common.settings.title')}</h3>
                  <p className="text-xs text-white/70">Szervezet beállítások</p>
                </div>
              </div>
            </div>
            <nav className="space-y-0.5">
              {availableTabs.map((tab) => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <div key={tab.id} className="relative">
                    <button
                      onClick={() => handleTabChange(tab.id)}
                      className={`
                        w-full flex items-center px-3 py-2 text-xs font-medium rounded-md transition-all duration-150 group relative
                        ${isActive
                          ? `${tab.activeColor} shadow-sm`
                          : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:text-gray-900 dark:hover:text-gray-100'
                        }
                      `}
                    >
                      <div className={`
                        flex items-center justify-center w-7 h-7 rounded-md mr-2.5 transition-all duration-150
                        ${isActive ? tab.bgColor : 'bg-gray-100 dark:bg-gray-700 group-hover:' + tab.bgColor}
                      `}>
                        <Icon className={`
                          h-4 w-4 transition-all duration-150
                          ${isActive
                            ? tab.iconColor
                            : 'text-gray-500 dark:text-gray-400 group-hover:' + tab.iconColor
                          }
                        `} />
                      </div>

                      <div className="text-left flex-1 min-w-0">
                        <div className={`font-medium truncate leading-tight ${isActive ? 'text-current' : ''}`}>
                          {tab.name}
                        </div>
                        <div className={`text-xs truncate leading-tight transition-colors duration-150 ${
                          isActive
                            ? 'text-current opacity-60'
                            : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-600 dark:group-hover:text-gray-300'
                        }`}>
                          {tab.description}
                        </div>
                      </div>

                      {isActive && (
                        <div className={`w-0.5 h-6 rounded-full absolute right-1 ${tab.iconColor.replace('text-', 'bg-')}`} />
                      )}
                    </button>
                  </div>
                );
              })}
            </nav>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-gray-900 w-full md:w-auto">
          <div className="p-6">
            {/* Organizations */}
            {activeTab === 'organizations' && (
              <OrganizationList
                selectedOrg={selectedOrg}
                onSelectOrg={setSelectedOrg}
                onEdit={() => setIsEditing(true)}
              />
            )}

            {/* MFA Policy */}
            {activeTab === 'mfa-policy' && (
              <MFAPolicySettings />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;